import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from "@/components/ui/field";
import { AdminPageProps, ProductType } from "@/types";
import { useForm, usePage } from "@inertiajs/react";
import { CheckIcon, CircleAlert, TriangleAlertIcon } from "lucide-react";
import { useState } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { api_search_product_path } from "@/lib/routes";
import { cn, getProductTypeLabel } from "@/lib/utils";
import useTranslations from "@/hooks/use-translations";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { csrfToken } from "@/lib/api";
import ImageWithFallback from "./image-with-fallback";
import { Badge } from "@/components/ui/badge";
import groupBy from "lodash/groupBy";
import uniq from "lodash/uniq";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { COLORS_BY_ID } from "@/lib/colors";

const VALUE_SEPARATOR = ":";

function PartPicker({
  items,
  onSelectedSystemCode,
}: {
  items: any[];
  onSelectedSystemCode: (values: {
    catalogable_id: number;
    product_type: string;
    system_code: string;
  }) => void;
}) {
  const { t } = useTranslations("shared.components.catalog_search_field");
  const itemsByPartNum = groupBy(items, "part_num");
  const uniqPartNums = uniq(items.map((item) => item.part_num));

  return (
    <FieldGroup>
      <Accordion type="multiple">
        {uniqPartNums.map((partNum) => (
          <AccordionItem key={partNum} value={partNum}>
            <AccordionTrigger>
              {itemsByPartNum[partNum][0].name}
            </AccordionTrigger>
            <AccordionContent>
              <RadioGroup
                onValueChange={(value) => {
                  const [product_type, system_code, catalogable_id] =
                    value.split(VALUE_SEPARATOR);
                  onSelectedSystemCode({
                    product_type,
                    system_code,
                    catalogable_id: Number(catalogable_id),
                  });
                }}
              >
                {itemsByPartNum[partNum].map((item) => {
                  const value = [
                    "part",
                    item.system_code,
                    item.catalogable_id,
                  ].join(VALUE_SEPARATOR);

                  return (
                    <FieldLabel key={value} htmlFor={value}>
                      <Field orientation="horizontal">
                        <ImageWithFallback
                          className="h-12 w-12 object-contain"
                          alt={item.name}
                          srcset={[
                            item.image_url,
                            `https://cdn.rebrickable.com/media/thumbs/parts/elements/${item.system_code}.jpg/250x250p.jpg`,
                            `https://cdn.rebrickable.com/media/thumbs/parts/ldraw/${item.color_id}/${item.part_num}.png/250x250p.png`,
                          ]}
                        />
                        <FieldContent>
                          <FieldTitle>
                            {COLORS_BY_ID[item.color_id].name}
                          </FieldTitle>
                          <FieldDescription>
                            {item.system_code}
                          </FieldDescription>
                          {item.already_in_store && (
                            <Badge>
                              <TriangleAlertIcon /> {t("already_in_store")}
                            </Badge>
                          )}
                        </FieldContent>
                        <RadioGroupItem value={value} id={value} />
                      </Field>
                    </FieldLabel>
                  );
                })}
              </RadioGroup>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </FieldGroup>
  );
}

export default function CatalogSearchField({
  productType,
  onSelectedSystemCode,
}: {
  productType: ProductType;
  onSelectedSystemCode: (values: {
    catalogable_id: number;
    product_type: string;
    system_code: string;
  }) => void;
}) {
  const { current_store, part_categories } = usePage<AdminPageProps>().props;
  const { t } = useTranslations("shared.components.catalog_search_field");

  const [resultsVisible, setResultsVisible] = useState<boolean>(true);
  const { data, setData } = useForm({ system_code: "", part_category_id: "" });
  const [fetchStatus, setFetchStatus] = useState<
    null | "processing" | "success" | "error"
  >(null);
  const [response, setResponse] = useState<
    | null
    | {
        type: ProductType;
        items: any[];
      }[]
  >(null);
  const processing = fetchStatus === "processing";
  const success = fetchStatus === "success";
  const error = fetchStatus === "error";

  async function onSearch() {
    if (!data.system_code) {
      return;
    }

    setFetchStatus("processing");

    try {
      const responseData = await fetch(
        api_search_product_path({
          format: "json",
          params: {
            store_id: current_store?.id,
            product_type: productType,
            system_code: data.system_code.trim(),
            part_category_id: data.part_category_id,
          },
        }),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
        }
      ).then((response) => response.json());

      setFetchStatus("success");
      setResponse(responseData);
    } catch (error) {
      console.error("Error fetching product:", error);
      setFetchStatus("error");
    }
  }

  let searchInputGroup = (
    <InputGroup
      data-disabled={processing}
      className={
        productType === "part"
          ? "group-focus:z-1 border-t-0 rounded-t-none"
          : undefined
      }
    >
      <InputGroupInput
        disabled={processing}
        id="system_code"
        name="product[system_code]"
        placeholder={t("search_placeholder")}
        value={data.system_code}
        onChange={(e) => setData("system_code", e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSearch();
          }
        }}
      />
      {processing && (
        <InputGroupAddon align="inline-end">
          <Spinner />
        </InputGroupAddon>
      )}
      {success && (
        <InputGroupAddon align="inline-end">
          <CheckIcon />
        </InputGroupAddon>
      )}
      {error && (
        <InputGroupAddon align="inline-end">
          <CircleAlert />
        </InputGroupAddon>
      )}
      <InputGroupAddon align="inline-end">
        <InputGroupButton variant="secondary" onClick={onSearch}>
          {t("search")}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );

  if (productType === "part") {
    searchInputGroup = (
      <div className="flex flex-col gap-0">
        <Select
          value={data.part_category_id}
          onValueChange={(value) => setData("part_category_id", value)}
        >
          <SelectTrigger className="w-full border-b-0 rounded-b-none focus:z-1">
            <SelectValue placeholder={t("category_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_categories")}</SelectItem>
            <SelectSeparator />
            {part_categories.map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="border-t">{searchInputGroup}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-5">
      <Field>
        {/* <FieldLabel htmlFor="system_code">Code</FieldLabel> */}
        {searchInputGroup}
      </Field>
      {success && response && productType !== "part" && (
        <FieldGroup>
          <RadioGroup
            onValueChange={(value) => {
              const [product_type, system_code, catalogable_id] =
                value.split(VALUE_SEPARATOR);
              onSelectedSystemCode({
                product_type,
                system_code,
                catalogable_id: Number(catalogable_id),
              });
            }}
          >
            {response.map((group) => {
              if (group.items.length === 0) {
                return null;
              }

              return (
                <FieldSet key={group.type}>
                  <FieldLegend className="flex justify-between items-center w-full">
                    <span>
                      {productType
                        ? t("results")
                        : getProductTypeLabel(group.type, t)}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      onClick={() => setResultsVisible((value) => !value)}
                    >
                      {resultsVisible ? t("hide") : t("show")}
                    </Button>
                  </FieldLegend>

                  <FieldGroup
                    className={cn("gap-4", resultsVisible ? "" : "hidden")}
                  >
                    {group.items.map((item: any) => {
                      const value = [
                        group.type,
                        item.system_code,
                        item.catalogable_id,
                      ].join(VALUE_SEPARATOR);

                      return (
                        <FieldLabel key={value} htmlFor={value}>
                          <Field orientation="horizontal">
                            {group.type === "part" ? (
                              <ImageWithFallback
                                className="h-12 w-12 object-contain"
                                alt={item.name}
                                srcset={[
                                  item.image_url,
                                  `https://cdn.rebrickable.com/media/thumbs/parts/elements/${item.system_code}.jpg/250x250p.jpg`,
                                  `https://cdn.rebrickable.com/media/thumbs/parts/ldraw/${item.color_id}/${item.part_num}.png/250x250p.png`,
                                ]}
                              />
                            ) : (
                              <ImageWithFallback
                                className="h-12 w-12 object-contain"
                                alt={item.name}
                                srcset={[item.image_url]}
                              />
                            )}
                            <FieldContent>
                              <FieldTitle>{item.name}</FieldTitle>
                              <FieldDescription>
                                {item.system_code}
                              </FieldDescription>
                              {item.already_in_store && (
                                <Badge>
                                  <TriangleAlertIcon /> {t("already_in_store")}
                                </Badge>
                              )}
                            </FieldContent>
                            <RadioGroupItem value={value} id={value} />
                          </Field>
                        </FieldLabel>
                      );
                    })}
                  </FieldGroup>
                </FieldSet>
              );
            })}
          </RadioGroup>
        </FieldGroup>
      )}
      {success && response && productType === "part" && (
        <PartPicker
          items={response[0].items ?? []}
          onSelectedSystemCode={onSelectedSystemCode}
        />
      )}
    </div>
  );
}
